import React, { useContext } from 'react'

import config from 'config'

export type SSRCookieConsentConfig = {
  ethycaEnabled?: boolean
  disabledSystems: Set<EthycaSystem>
  isAdsEnabled?: boolean
}

export type SSRCookieConsentContextType = {
  setEthycaEnabled: (enabled: boolean) => void
  addDisabledSystem: (system: EthycaSystem) => void
  setIsAdsEnabled: (enabled: boolean) => void
}

const SSRCookieConsentContext =
  React.createContext<SSRCookieConsentContextType>({
    setEthycaEnabled: () => {},
    addDisabledSystem: () => {},
    setIsAdsEnabled: () => {}
  })

export type Props = {
  ssrCookieConfig: SSRCookieConsentConfig
}

export type EthycaSystem =
  | 'meta'
  | 'userway'
  | 'google_analytics'
  | 'google_ads'
  | 'pendo'
  | 'heap'
  | 'tealium'
  | 'toast_guest_analytics'
  | 'google_tag_manager'
  | 'third_party_embedded_widgets'
/**
 * This context is used to set values that will be used during server side rendering
 * to configure cookie consent correctly.
 *
 * WARNING: because we need these values during server side rendering, we cannot
 * use react hooks like useState or useEffect that rely on rerendering - there is no
 * rerendering during server side rendering. Instead we set state directly. Because
 * of this, the state in the context should only be read during server side rendering.
 */
export const SSRCookieConsentContextProvider = ({
  ssrCookieConfig,
  children
}: React.PropsWithChildren<Props>) => {
  const setEthycaEnabled = (enabled: boolean) => {
    ssrCookieConfig.ethycaEnabled = enabled
  }
  const addDisabledSystem = (system: EthycaSystem) => {
    ssrCookieConfig.disabledSystems.add(system)
  }
  const setIsAdsEnabled = (enabled: boolean) => {
    ssrCookieConfig.isAdsEnabled = enabled
  }
  const context = {
    setEthycaEnabled,
    addDisabledSystem,
    setIsAdsEnabled
  }
  return (
    <SSRCookieConsentContext.Provider value={context}>
      {children}
    </SSRCookieConsentContext.Provider>
  )
}

export const useSSRCookieConsentContext = () => {
  return useContext(SSRCookieConsentContext)
}

/**
 * This is a convenience wrapper script provided by the Ethyca team.
 * It allows us to override some behaviors of Ethyca for testing purposes at
 * runtime. For example, we can set fides_cache_bust=true, and fides will get the
 * latest Ethyca configuration instead of the cached version.
 */
export const generateEthycaScript = (
  ooEthycaAds: boolean,
  disabledSystems: Set<EthycaSystem>,
  isAdsEnabled: boolean
): string => {
  const disabledSystemsParams = createDisabledSystemsParams(
    Array.from(disabledSystems)
  )

  // If Ads is enabled, then it uses the new Ads Manager property id.
  const propertyId = isAdsEnabled
    ? config.ethycaPropertyIdAds
    : config.ethycaPropertyId

  if ((ooEthycaAds ?? false) && isAdsEnabled) {
    return ooEthycaAdsScript(propertyId, disabledSystemsParams)
  } else {
    return `(function () {
    // Helps with debugging by loading all the details of the cookie banner,
    // even if they won't be displayed for a given users geolocation.
    window.fides_overrides = {
      fides_consent_non_applicable_flag_mode: "include",
      fides_consent_flag_type: "boolean"
    };
    // Adds extra debugging information when Ethyca is managing consent for GTM.
    addEventListener("FidesInitializing", function () {
      window.Fides.gtm({
        "non_applicable_flag_mode": "include",
        "flag_type": "boolean",
      });
    });
    // Goes through the url query params of the website/oo page looking for ones starting with fides_
    var fidesPrefix = "fides_";
    var searchParams = new URLSearchParams(location.search);
    var fidesSearchParams = new URLSearchParams();
    var disabledSystemsParamName = "fides_disabled_systems";
    var disabledSystemsParam = "${disabledSystemsParams}";
    for (var entry of searchParams.entries()) {
      var key = entry[0];
      var value = entry[1];
      if (key === disabledSystemsParamName) {
        // fides_disabled_systems is a special case because unlike the others,
        // we need to set this even for non-debugging scenarios. So here, if we find
        // fides_disabled_systems in the URL, it acts as an override. But if we don't,
        // we set it the value passed in to generateEthycaScript.
        disabledSystemsParam = "&" + disabledSystemsParamName + "=" + value;
        continue;
      }
      if (key.startsWith(fidesPrefix)) {
        // There is special handling for fides_cache_bust - its value is set to the
        // current timestamp. This is because the cache_bust requires a unique value
        // to avoid hitting Ethyca's cache.
        fidesSearchParams.set(
          key.replace(fidesPrefix, ""),
          key === fidesPrefix + "cache_bust" ? Date.now().toString() : value
        );
      }
    }
    var src = "https://toast.fides-cdn.ethyca.com/fides.js?" + fidesSearchParams.toString() + "&property_id=" + "${propertyId}" + disabledSystemsParam;
    var script = document.createElement("script");
    script.setAttribute("src", src);
    script.setAttribute("id", "fides-js");
    document.head.appendChild(script);
  })();`
  }
}

const createDisabledSystemsParams = (disabledSystems: EthycaSystem[]) => {
  return disabledSystems.length > 0
    ? `&fides_disabled_systems=${disabledSystems.join(',')}`
    : ''
}

// DISCLAIMER:
// This is a temporary implementation of a `visted-sites` cookie to track if a user has visited a site.
// It is a stop-gap measure to solve a limitation in Ethyca's cookie consent where it is only mapped to the domain, and not our individual Rx locations.
// This is not a long-term solution and is only intended to be used for Ads Manager GA and should be replaced when Ethyca supports per-location cookie consent.
// DO NOT EXTEND THE COOKIE'S LOGIC BEYOND WHAT IS IMPLEMENTED HERE WITHOUT PRIOR CONSULT WITH LEGAL.

const ooEthycaAdsScript = (
  propertyId: string,
  disabledSystemsParams: string
): string => {
  return `(function () {
    // hasUserVisitedThisSite implementation (plain JavaScript)
    var COOKIE_NAME = 'visited_sites';
    var COOKIE_MAX_AGE_DAYS = 180;
    var VISIT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

    function getCookie(name) {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match && match[1] ? decodeURIComponent(match[1]) : null;
    }

    function setCookie(name, value, days) {
      var expires = new Date();
      expires.setDate(expires.getDate() + days);
      var secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;expires=' + expires.toUTCString() + ';SameSite=Lax' + secure;
    }

    function readVisitMap() {
      try {
        var raw = getCookie(COOKIE_NAME);
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        return {};
      }
    }

    function writeVisitMap(map) {
      setCookie(COOKIE_NAME, JSON.stringify(map), COOKIE_MAX_AGE_DAYS);
    }

    function buildSiteKey() {
      var hostname = window.location.hostname;

      // if hostname is not a toasttab domain, then return the hostname
      if (!hostname.includes('toasttab.com')) {
        return hostname;
      }

      var pathname = window.location.pathname.replace(new RegExp('/+$'), '') || '/';

      // truncate pathname to include only the segment immediately after /online/
      var pathnameParts = pathname.split('/');
      var onlineIndex = pathnameParts.indexOf('online');
      if (onlineIndex !== -1 && onlineIndex + 1 < pathnameParts.length) {
        // Include 'online' and only the next segment (restaurant name)
        var truncatedPathname = pathnameParts.slice(onlineIndex, onlineIndex + 2).join('/');
      } else {
        // Fallback: if /online/ not found, use original pathname
        var truncatedPathname = pathname;
      }

      return hostname + '|' + truncatedPathname;
    }

    function hasUserVisitedThisSite() {      
      var key = buildSiteKey();
      var visits = readVisitMap();
      var now = Date.now();
      var lastVisited = visits[key] || null;
      var hasVisited = false;

      if (lastVisited && now - lastVisited < VISIT_EXPIRY_MS) {
        hasVisited = true;
      }
      // Update this sites timestamp now
      visits[key] = now

      // Optional cleanup: remove stale entries to keep cookie small
      for (const [k, ts] of Object.entries(visits)) {
        if (now - ts > VISIT_EXPIRY_MS * 2) {
          delete visits[k]
        }
      }

      writeVisitMap(visits)

      return hasVisited;
    }

    var hasVisited = hasUserVisitedThisSite();

    // Overrides Information:
    // Helps with debugging by loading all the details of the cookie banner,
    // even if they won't be displayed for a given users geolocation.
    // The value of fides_clear_cookie is the inverse of whether the user has visited this site.
    // If the user has visited this site, then fides_clear_cookie is false and we respect existing settings.
    // If the user has not visited this particular site, then fides_clear_cookie is true and we aim to show the cookie banner.

    window.fides_overrides = {
      fides_consent_non_applicable_flag_mode: "include",
      fides_consent_flag_type: "boolean",
      fides_clear_cookie: !hasVisited ?? false
    };
    // Adds extra debugging information when Ethyca is managing consent for GTM.
    addEventListener("FidesInitializing", function () {
      window.Fides.gtm({
        "non_applicable_flag_mode": "include",
        "flag_type": "boolean",
      });
    });
    // Goes through the url query params of the website/oo page looking for ones starting with fides_
    var fidesPrefix = "fides_";
    var searchParams = new URLSearchParams(location.search);
    var fidesSearchParams = new URLSearchParams();
    var disabledSystemsParamName = "fides_disabled_systems";
    var disabledSystemsParam = "${disabledSystemsParams}";
    for (var entry of searchParams.entries()) {
      var key = entry[0];
      var value = entry[1];
      if (key === disabledSystemsParamName) {
        // fides_disabled_systems is a special case because unlike the others,
        // we need to set this even for non-debugging scenarios. So here, if we find
        // fides_disabled_systems in the URL, it acts as an override. But if we don't,
        // we set it the value passed in to generateEthycaScript.
        disabledSystemsParam = "&" + disabledSystemsParamName + "=" + value;
        continue;
      }
      if (key.startsWith(fidesPrefix)) {
        // There is special handling for fides_cache_bust - its value is set to the
        // current timestamp. This is because the cache_bust requires a unique value
        // to avoid hitting Ethyca's cache.
        fidesSearchParams.set(
          key.replace(fidesPrefix, ""),
          key === fidesPrefix + "cache_bust" ? Date.now().toString() : value
        );
      }
    }
    var src = "https://toast.fides-cdn.ethyca.com/fides.js?" + fidesSearchParams.toString() + "&property_id=" + "${propertyId}" + disabledSystemsParam;
    var script = document.createElement("script");
    script.setAttribute("src", src);
    script.setAttribute("id", "fides-js");
    document.head.appendChild(script);
  })();`
}
